// Activity Feed Types

export type ActivityType =
  | 'pregnancy_created'
  | 'pregnancy_birth'
  | 'pregnancy_ended'
  | 'ssw_milestone'
  | 'ssw_change'
  | 'partner_connected'
  | 'partner_disconnected'
  | 'checklist_item_completed'
  | 'checklist_completed'
  | 'name_match'
  | 'name_favorited'
  | 'appointment_created'
  | 'appointment_completed'
  | 'baby_care_feeding'
  | 'baby_care_diaper'
  | 'baby_care_sleep'
  | 'baby_care_medication'
  | 'baby_care_temperature'
  | 'baby_care_note';

export interface ActivityFeedEntry {
  id: string;
  user_id: string;
  pregnancy_id: string | null;
  activity_type: ActivityType;
  activity_date: string;
  title: string;
  description: string | null;
  metadata: ActivityMetadata | null;
  actor_user_id: string | null;
  actor_display_name: string | null;
  created_at: string;
}

export interface ActivityMetadata {
  // For SSW milestones
  ssw?: number;
  developments?: string[];
  icon?: string; // Lucide icon name from database

  // For name matches
  name_id?: string;
  name?: string;

  // For partner events
  partner_id?: string;
  partner_name?: string;

  // For checklist events
  checklist_id?: string;
  checklist_title?: string;
  item_title?: string;

  // For birth events
  birth_date?: string;
  birth_time?: string;
  baby_data?: {
    weight_grams?: number;
    height_cm?: number;
    head_circumference_cm?: number;
    notes?: string;
    babies?: Array<{
      name?: string;
      gender?: string;
      weight_grams?: number;
      height_cm?: number;
    }>;
  };

  // For ended events
  ended_by?: string;

  // For baby care events
  care_type?: 'feeding' | 'diaper' | 'sleep' | 'medication' | 'temperature' | 'notes';
  feeding_type?: 'breast' | 'bottle' | 'solid';
  feeding_amount_ml?: number;
  diaper_type?: 'wet' | 'dirty' | 'both' | 'dry';
  sleep_duration_minutes?: number;
  medication_name?: string;
  temperature_value?: number;

  // Generic
  [key: string]: unknown;
}

export interface ActivityFeedConfig {
  enabled_types: ActivityType[];
  dashboard_count: number;
}

// Lucide icon names for activity types
export type LucideIconName =
  | 'Baby'
  | 'PartyPopper'
  | 'FileText'
  | 'Milestone'
  | 'CalendarDays'
  | 'Users'
  | 'UserMinus'
  | 'CheckSquare'
  | 'ListChecks'
  | 'Heart'
  | 'Star'
  | 'Calendar'
  | 'CalendarCheck'
  | 'Sparkles'
  | 'Trophy'
  | 'Cake'
  | 'Utensils'
  | 'Droplets'
  | 'Moon'
  | 'Pill'
  | 'Thermometer'
  | 'StickyNote';

// Activity type display configuration with Lucide icon names
// All icons use white color on orange background for consistency
export const ACTIVITY_TYPE_CONFIG: Record<ActivityType, {
  icon: LucideIconName;
  color: string;
  bgColor: string;
  label: string;
}> = {
  pregnancy_created: {
    icon: 'Baby',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'Schwangerschaft gestartet',
  },
  pregnancy_birth: {
    icon: 'PartyPopper',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'Geburt',
  },
  pregnancy_ended: {
    icon: 'FileText',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'Schwangerschaft beendet',
  },
  ssw_milestone: {
    icon: 'Milestone',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'SSW-Meilenstein',
  },
  ssw_change: {
    icon: 'CalendarDays',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'SSW-Wechsel',
  },
  partner_connected: {
    icon: 'Users',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'Partner verbunden',
  },
  partner_disconnected: {
    icon: 'UserMinus',
    color: 'text-white/50',
    bgColor: 'bg-pp-orange/50',
    label: 'Partner getrennt',
  },
  checklist_item_completed: {
    icon: 'CheckSquare',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'Item abgehakt',
  },
  checklist_completed: {
    icon: 'ListChecks',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'Checkliste abgeschlossen',
  },
  name_match: {
    icon: 'Heart',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'Namen-Match',
  },
  name_favorited: {
    icon: 'Star',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'Name favorisiert',
  },
  appointment_created: {
    icon: 'Calendar',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'Termin erstellt',
  },
  appointment_completed: {
    icon: 'CalendarCheck',
    color: 'text-white',
    bgColor: 'bg-pp-orange',
    label: 'Termin wahrgenommen',
  },
  baby_care_feeding: {
    icon: 'Utensils',
    color: 'text-white',
    bgColor: 'bg-teal-500',
    label: 'Fütterung',
  },
  baby_care_diaper: {
    icon: 'Droplets',
    color: 'text-white',
    bgColor: 'bg-blue-500',
    label: 'Windel',
  },
  baby_care_sleep: {
    icon: 'Moon',
    color: 'text-white',
    bgColor: 'bg-indigo-500',
    label: 'Schlaf',
  },
  baby_care_medication: {
    icon: 'Pill',
    color: 'text-white',
    bgColor: 'bg-rose-500',
    label: 'Medikament',
  },
  baby_care_temperature: {
    icon: 'Thermometer',
    color: 'text-white',
    bgColor: 'bg-amber-500',
    label: 'Temperatur',
  },
  baby_care_note: {
    icon: 'StickyNote',
    color: 'text-white',
    bgColor: 'bg-slate-500',
    label: 'Notiz',
  },
};
